import type { ChampDuProfil, FeuilleDeProfil } from '@nextilt/commun';
import type { ContactDuProfil, LigneDeProfil } from '../api/types';

/**
 * Les mots de la page Profil de l'ordinateur (chantier profil, ticket 02, maquette du carnet
 * `profil.html`) : ses onglets, ses feuilles, ses champs, d'où vient chaque info. Le nom de la
 * page est `PROFIL`, dans `cadre.ts`.
 */

/*
 * Sous le nom, la phrase de la maquette : l'IA propose, depuis le chantier profil-pour-claude
 * (ticket 02).
 */
export const SOUS_TITRE_DU_PROFIL = "Ce que Nextilt sait de toi. Tu l'écris, ou ton IA le propose et tu valides. Chaque info dit d'où elle vient.";
export const COMPLETER_MON_PROFIL = 'Compléter mon profil';

/** Les onglets de la boîte à fiches, dans l'ordre de la maquette. */
export const ONGLET_TOUT = 'Tout';
export const ONGLET_MOI = 'Moi';
export const ONGLET_MES_MOYENS = 'Mes moyens';
export const ONGLET_MON_RYTHME = 'Mon rythme';
export const ONGLET_MES_CONTACTS = 'Mes contacts';
export const ONGLET_PROPOSE_PAR_L_IA = "Proposé par l'IA";
export const ONGLET_SENSIBLE = 'Sensible';

/** Les feuilles, sous les onglets. */
export const FEUILLE_QUI_JE_SUIS = 'Qui je suis';
export const FEUILLE_CE_QUE_J_AI = "Ce que j'ai";
export const FEUILLE_MA_FACON_DE_FAIRE = 'Ma façon de faire';
export const FEUILLE_SENSIBLE = 'Sensible';
export const FEUILLE_PROPOSE_PAR_TON_IA = 'Proposé par ton IA';
export const FEUILLE_MES_CONTACTS = 'Mes contacts';

/** Le titre des trois feuilles qui s'écrivent, sur la page et dans le premier formulaire (ticket 03). */
export const TITRES_DES_FEUILLES_DU_PROFIL: Record<FeuilleDeProfil, string> = {
  'qui-je-suis': FEUILLE_QUI_JE_SUIS,
  'ce-que-j-ai': FEUILLE_CE_QUE_J_AI,
  'ma-facon-de-faire': FEUILLE_MA_FACON_DE_FAIRE,
};

/**
 * Le premier formulaire (chantier profil, ticket 03, décision 5 d'Igor) : par-dessus l'accueil,
 * des cases vides permises, « Plus tard ». Il se rouvre par « Compléter mon profil ».
 */
export const TITRE_DU_FORMULAIRE_DU_PROFIL = 'Ton profil';
export const PHRASE_DU_FORMULAIRE_DU_PROFIL =
  'Écris ce que tu veux : chaque case peut rester vide. Tout se corrige ensuite dans Profil.';
export const PLUS_TARD_DU_PROFIL = 'Plus tard';
export const ENREGISTRER_LE_PROFIL = 'Enregistrer';

/**
 * Le nom de chaque champ, à gauche de sa valeur (maquette `.info span`) : une question en clair
 * plutôt qu'un mot seul (retour d'Igor au test, 2026-10-21 : « situation mon rythme et courage
 * n'es pas claire pour moi alors un nouveau utilisateur c'est pire », choix A).
 */
export const MOTS_DES_CHAMPS_DU_PROFIL: Record<ChampDuProfil, string> = {
  prenom: 'Prénom',
  ville: 'Ville',
  quartier: 'Code postal ou quartier',
  situation: 'Ce que tu fais dans la vie',
  enRouteVers: 'Ce vers quoi tu vas',
  permis: 'Permis de conduire',
  voiture: 'Voiture',
  rythme: 'Quand as-tu du temps ?',
  courage: 'Ton énergie en semaine',
  // Les quatre du chantier profil-pour-claude (ticket 02), en question comme les autres.
  lieuDeTravail: 'Où tu travailles',
  heuresDeTravail: 'Tes heures de travail',
  deplacement: 'Comment tu te déplaces',
  meilleurMoment: 'Ton meilleur moment',
};

/** Un exemple, grisé dans la case vide : ce qu'on attend, sans rien imposer. */
export const EXEMPLES_DES_CHAMPS_DU_PROFIL: Record<ChampDuProfil, string> = {
  prenom: 'Ton prénom',
  ville: 'Lyon, Annecy…',
  quartier: '73600, le centre-ville…',
  situation: 'responsable de bar, étudiant, en reconversion…',
  enRouteVers: 'devenir développeur, acheter une maison…',
  permis: 'oui, non, en cours…',
  voiture: 'oui, non, celle du travail…',
  rythme: 'le soir, le week-end, peu l’hiver…',
  courage: '',
  lieuDeTravail: 'à la maison, au bureau à Albertville…',
  heuresDeTravail: '9 h à 17 h en semaine, le soir en saison…',
  deplacement: 'à pied, en bus, en voiture…',
  meilleurMoment: 'le matin, après le déjeuner…',
};

/** D'où vient une info tapée (maquette `.info em`), en petit sous elle : la maquette l'écrit sans capitale. */
export const ECRIT_PAR_TOI = 'écrit par toi';
/** Un champ vide invite à le remplir. */
export const A_REMPLIR = 'À remplir';
/** Une ligne libre sans intitulé. */
export const SANS_INTITULE = 'Intitulé';
/** « + ajouter une info », au bas d'une feuille (maquette `.ajouter-info`). */
export const AJOUTER_UNE_INFO = '+ ajouter une info';
export const INTITULE_DE_L_INFO = 'Intitulé';
export const VALEUR_DE_L_INFO = 'Valeur';
export const RETIRER_L_INFO = 'Retirer cette info';
/** Le nom accessible d'une case qui s'ouvre pour corriger. */
export function corriger(nom: string): string {
  return `Corriger : ${nom}`;
}

/** La feuille Sensible, fermée (maquette `.verrou p`). */
export const EXPLICATION_DU_SENSIBLE =
  "Salaire, crédit, budget, adresse précise : pour qu'un projet serve aux autres (le prêt de la maison et le budget d'une voiture). Arrive après la sécurité du serveur.";
/**
 * « Proposé par ton IA » (chantier profil-pour-claude, ticket 02, maquette `.propose`, `.prop`) :
 * chaque proposition entre guillemets, d'où elle vient en petit, Garder et Non.
 */
export const GARDER_LA_PROPOSITION = 'Garder';
export const REFUSER_LA_PROPOSITION = 'Non';
/** La feuille sans proposition. */
export const PAS_DE_PROPOSITION = 'Rien à garder pour l’instant. Quand ton IA te propose une info, elle t’attend ici.';
/** Sous la valeur d'une ligne gardée (maquette `.info em`), sans capitale comme « écrit par toi ». */
export const PROPOSE_PAR_CLAUDE_GARDE = 'proposé par Claude, gardé';

/** Ce que Claude propose : son intitulé et sa valeur ; sans intitulé, la valeur (le téléphone l'écrit tel quel, à la main). */
export function ditDeLaProposition(ligne: Pick<LigneDeProfil, 'intitule' | 'valeur'>): string {
  return ligne.intitule === '' ? ligne.valeur : `${ligne.intitule} : ${ligne.valeur}`;
}

/** Ce que Claude propose, entre guillemets (l'ordinateur). */
export function texteDeLaProposition(ligne: Pick<LigneDeProfil, 'intitule' | 'valeur'>): string {
  return `« ${ditDeLaProposition(ligne)} »`;
}

/** D'où vient une proposition, en petit dessous : « Claude, en créant le projet … ». */
export function origineDeLaProposition(ligne: Pick<LigneDeProfil, 'origine'>): string {
  return ligne.origine ? `Claude, ${ligne.origine}` : 'Claude';
}

/** Sous la valeur d'une ligne : gardée d'une proposition, « proposé par Claude, gardé » ; écrite par toi, « écrit par toi » ; vide, rien. */
export function mentionDeLaLigne(ligne: Pick<LigneDeProfil, 'origine' | 'valeur'>): string | null {
  if (ligne.origine !== null) return PROPOSE_PAR_CLAUDE_GARDE;
  return ligne.valeur !== '' ? ECRIT_PAR_TOI : null;
}

/** Le nom accessible du compte de l'onglet. */
export function propositionsATrier(n: number): string {
  return n === 1 ? '1 proposition à garder ou non' : `${n} propositions à garder ou non`;
}

/** Mes contacts, sans contact : d'où ils viendront. */
export const PAS_DE_CONTACT = 'Tes contacts viennent des fiches de tes bulles : un nom, un téléphone ou un e-mail écrit dans une fiche apparaît ici.';

/**
 * D'où vient un contact (maquette `.info em`) : sa bulle et son projet, « Appeler le plombier ·
 * Salle de bain » ; la bulle est le projet, le projet seul. Un même numéro réunit ses projets.
 */
export function origineDuContact(contact: ContactDuProfil): string {
  const projets = contact.projets.map((p) => p.titre).join(', ');
  if (contact.projets[0]?.id === contact.bulle.id) return projets;
  return `${contact.bulle.titre} · ${projets}`;
}

/** Un geste du profil refusé par le serveur, ou parti sans réseau. */
export function profilPasEcrit(code: string): string {
  if (code === 'plafond_de_lignes') return 'Cette feuille a déjà le nombre maximal d’infos';
  if (code === 'hors_ligne') return 'Hors ligne : rien n’est écrit, réessaie quand le réseau revient';
  return 'Ton profil n’a pas pu être écrit, réessaie';
}

/*
 * Le Profil au téléphone (chantier telephone-profil, 01, maquette `app-au-telephone.html`,
 * `profil()`, `sectionProfil()`, `feuilleHtml()` branche `champ:`) : la ligne des Paramètres, le
 * haut, les intercalaires et leur compte, la feuille qui corrige une ligne.
 */
export const MON_PROFIL = 'Mon profil';
export const DESSUS_DU_PROFIL_AU_POUCE = 'ce que Nextilt et Claude savent de toi';
/** Au téléphone, l'intercalaire garde le nom de la maquette (à l'ordinateur : « Proposé par ton IA »). */
export const FEUILLE_PROPOSE_PAR_CLAUDE = 'Proposé par Claude';
/** Le compte de Qui je suis : « 5 sur 6 » remplis. */
export function remplisSur(remplis: number, total: number): string {
  return `${remplis} sur ${total}`;
}
/** Le compte de Proposé par Claude. */
export function aTrier(n: number): string {
  return `${n} à trier`;
}
export const SENSIBLE_FERME = 'fermé';
export const CONTACTS_RELUS = 'relus dans tes bulles : corrige-les dans la bulle';
export const PROPOSITIONS_DE_CLAUDE = 'ce que Claude a appris en parlant avec toi ; rien n’entre sans toi';
export const FERME_POUR_L_INSTANT = 'Fermé pour l’instant';
export const EXPLICATION_DU_SENSIBLE_AU_POUCE =
  'ton adresse exacte, ton budget… viendront après la sécurité du serveur, et tu choisiras ce que Claude peut voir.';
/** Sous la case de la feuille qui corrige une ligne. */
export const CLAUDE_LE_LIT = 'Claude le lit pour te poser moins de questions.';
export const UNE_INFO_A_TOI = 'Une info à toi';
/** Les crans du permis et de la voiture (choix de Claude au lancement du chantier) : le mot choisi s'écrit dans le champ. */
export const CRANS_DU_PERMIS = ['Non', 'En cours', 'Oui'] as const;
export const CRANS_DE_LA_VOITURE = ['Non', 'Oui', 'Du travail'] as const;
/** Le chemin d'une feuille de correction : « Mon profil · Qui je suis ». */
export function cheminDansLeProfil(feuille: string): string {
  return `${MON_PROFIL} · ${feuille}`;
}
